/**
 * Sentry observability utilities for backend API errors.
 *
 * Centralizes how we report 5xx responses from the Hey API client so they
 * group cleanly in Sentry. Wired into the client error interceptor in App.tsx.
 */
import { captureException } from '@sentry/react';

/**
 * Replaces ids in a URL path with `:id` so server errors group by route in
 * Sentry instead of fragmenting per policy/entity id. Returns `'unknown'` for
 * missing or unparseable URLs.
 * @param rawUrl - The full request/response URL.
 */
export const normalizeApiPath = (rawUrl: string | undefined): string => {
  if (!rawUrl) {
    return 'unknown';
  }
  try {
    return new URL(rawUrl).pathname.replace(
      /\/(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)(?=\/|$)/gi,
      '/:id'
    );
  } catch {
    return 'unknown';
  }
};

/**
 * Reduces a response body to the fields that are safe to ship to Sentry. A 5xx
 * body can echo submitted field values, so we never forward the whole payload:
 * strings are truncated, objects keep only `{ detail, code, message }`. Mirrors
 * the route-metadata-only convention in `allauth.ts`.
 * @param body - The raw response body from the error interceptor.
 */
const toSafeResponseBody = (body: unknown): string => {
  if (typeof body === 'string') {
    return body.slice(0, 1000);
  }
  try {
    const { detail, code, message } = (body ?? {}) as Record<string, unknown>;
    return JSON.stringify({ detail, code, message });
  } catch {
    return '';
  }
};

/**
 * Reports a backend 5xx response to Sentry with a stable message + route
 * fingerprint so failures group and stay traceable. No-op for non-5xx
 * responses. Previously these surfaced as opaque "<unknown>" events carrying
 * whole HTML error pages (ASPEN-DENTAL-3T).
 * @param body - The response body from the error interceptor.
 * @param response - The fetch Response, when available.
 * @param request - The originating fetch Request, when available.
 */
export const reportServerError = (
  body: unknown,
  response: Response | undefined,
  request: Request | undefined
): void => {
  const status = response?.status;
  if (!status || status < 500) {
    return;
  }

  const method = request?.method ?? 'UNKNOWN';
  const path = normalizeApiPath(response?.url ?? request?.url);

  captureException(new Error(`API ${status} ${method} ${path}`), {
    tags: { api_status: status, api_method: method, api_path: path },
    fingerprint: ['api-server-error', method, path, String(status)],
    extra: {
      status,
      method,
      // Normalized path only — the raw URL carries ids and query params we just
      // stripped (consistent with allauth.ts `extra: { method, path }`).
      url: path,
      responseBody: toSafeResponseBody(body),
    },
  });
};

/**
 * Ceiling on the mutation payload shipped to Sentry — a runaway payload would
 * push the event past Sentry's ~1MB limit and cost us the report entirely. Same
 * 1000 as `toSafeResponseBody` above, so this file has one truncation rule.
 */
const MAX_PAYLOAD_CHARS = 1000;

/**
 * Longest path segment we treat as a route literal. The longest real one is
 * `commercial-property-locations` (29); Django's password-reset token is ~39.
 */
const MAX_ROUTE_SEGMENT_CHARS = 32;

/**
 * Page label for a Sentry event, reduced to route literals: every segment that
 * isn't one is an id. `normalizeApiPath` only strips digits and UUIDs, which on
 * `/auth/reset-password/:userId/:token` leaves a live reset token in the event
 * title, tags and fingerprint.
 * @param pathname - `window.location.pathname`.
 */
const toPageName = (pathname: string): string =>
  pathname.replace(/[^/]+/g, (segment) =>
    // Every route literal in routes.tsx is letters and hyphens only.
    /^[a-z-]+$/i.test(segment) && segment.length <= MAX_ROUTE_SEGMENT_CHARS
      ? segment
      : ':id'
  );

/**
 * Serializes mutation variables for Sentry: capped, and non-throwing so a bad
 * payload can't turn a reportable failure into an unreportable one.
 * @param variables - The variables the mutation was called with.
 */
const toPayload = (variables: unknown): string => {
  try {
    return JSON.stringify(variables).slice(0, MAX_PAYLOAD_CHARS);
  } catch {
    return '';
  }
};

/** Per-mutation context the `MutationCache` hands to `reportTransportFailure`. */
export interface TransportFailureContext {
  /** Whether the mutation opted into shipping `variables` (`meta.reportPayload`). */
  reportPayload?: boolean;
  /** Human label for the endpoint (`meta.endpoint`); hey-api exposes no URL here. */
  endpoint?: string;
  /** Milliseconds between the mutation being submitted and the rejection. */
  elapsedMs?: number;
}

/**
 * Coarse bucket for `elapsedMs`, as a tag so the failures are filterable in
 * Sentry: sub-second means the browser never got the request out (offline,
 * proxy refusing), tens of seconds means something cut a connection that
 * was already open. The exact number rides along in `extra`.
 * @param elapsedMs - Time in flight before the rejection.
 */
const toElapsedBucket = (elapsedMs: number): string => {
  if (elapsedMs < 1_000) return '<1s';
  if (elapsedMs < 10_000) return '1-10s';
  if (elapsedMs < 60_000) return '10-60s';
  return '>60s';
};

/**
 * Reports a mutation whose request never produced a response. The hey-api
 * client awaits `fetch()` with no try/catch, so a transport failure rejects
 * before `interceptors.error` runs — `reportServerError` never sees it, and the
 * failure stops at whatever toast the caller happens to show. That is what left
 * DC-3628 ("Failed to fetch" on Accept Changes) with no record on either side.
 *
 * Wired into the app's `MutationCache` in `queryClient.ts` so it covers every
 * mutation without each one opting in. Queries are deliberately not covered:
 * they retry and the user can refresh, while a failed write leaves us unable to
 * say whether it committed.
 *
 * The payload is opt-in per mutation (`meta: { reportPayload: true }`). A
 * request that vanished mid-flight leaves no trace of what was being written,
 * and not knowing that is what made DC-3628 unreproducible — but the cache also
 * sees Login, ResetPassword and MFAVerify, whose variables are the credentials
 * themselves, and allauth rethrows transport failures as a plain `Error` with
 * no `status`, so the guard below doesn't filter them out. Opting in is how a
 * mutation declares its payload safe to ship.
 *
 * DC-3761: the event also carries how long the request was in flight, which
 * endpoint it was, and whether the browser thought it was online — the three
 * facts that were missing when the same failure had to be diagnosed by
 * cross-referencing Snowflake by timestamp.
 * @param error - The mutation's rejection.
 * @param variables - The hey-api `Options` the mutation was called with.
 * @param context - Opt-in payload flag plus endpoint/timing from the mutation.
 */
export const reportTransportFailure = (
  error: unknown,
  variables: unknown,
  { reportPayload = false, endpoint, elapsedMs }: TransportFailureContext = {}
): void => {
  // The cache sees every mutation error, not only transport ones. Anything
  // carrying a `status` came back from the server, so the error interceptor
  // already ran and `reportServerError` has it covered.
  if (error && typeof error === 'object' && 'status' in error) {
    return;
  }
  // Navigation or unmount cancelling an in-flight request — not a failure.
  if (error instanceof DOMException && error.name === 'AbortError') {
    return;
  }

  const page = toPageName(window.location.pathname);

  // Synthetic Error, same as `reportServerError`: the issue title has to carry
  // the location, since every transport failure shares an identical stack
  // inside the fetch client. The browser's own message is also engine-specific
  // ("Failed to fetch" / "Load failed" / "NetworkError…") and would fragment
  // grouping across Chrome, Safari and Firefox.
  captureException(new Error(`API no response on ${page}`), {
    tags: {
      api_status: 'no_response',
      page,
      endpoint: endpoint ?? 'unknown',
      online: String(navigator.onLine),
      ...(elapsedMs !== undefined && {
        elapsed_bucket: toElapsedBucket(elapsedMs),
      }),
    },
    fingerprint: ['api-no-response', page],
    extra: {
      originalError: String(error),
      ...(elapsedMs !== undefined && { elapsedMs }),
      // hey-api `Options` — `{ path, body }`. The URL isn't reachable from here
      // (hey-api generates no mutationKey), so `path` is what identifies the
      // affected policy; the exact endpoint is in the fetch breadcrumb.
      ...(reportPayload ? { variables: toPayload(variables) } : {}),
    },
  });
};
